import { Component } from "react";
import PropTypes from "prop-types";
import { Button, Result, Space, Typography } from "antd";

import { isDev } from "@/config";

// Errors that should only appear in the browser console, not as a full-page UI.
// These are typically React runtime issues (infinite loops, update cycles) that
// are transient or caused by third-party library internals.
const CONSOLE_ONLY_ERROR_PATTERNS = [
    "Maximum update depth exceeded",
];

export default class AppErrorBoundary extends Component {
    static defaultProps = {
        children: null,
        fallback: null,
        onError: null,
        onReset: null,
        title: "Something went wrong",
    };

    static propTypes = {
        children: PropTypes.node,
        fallback: PropTypes.node,
        onError: PropTypes.func,
        onReset: PropTypes.func,
        title: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
        };
        this.handleReset = this.handleReset.bind(this);
        this.handleReload = this.handleReload.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        const { onError } = this.props;
        const normalizedError =
            error instanceof Error
                ? error
                : new Error(
                    typeof error === "string" && error
                        ? error
                        : "Unknown render error"
                );
        if (typeof onError === "function") {
            onError(normalizedError, info);
        }
        // Always log to console so errors are visible in DevTools regardless of type.
        console.error("[AppErrorBoundary]", normalizedError, info);
    }

    handleReset() {
        const { onReset } = this.props;
        this.setState({ hasError: false, error: null });
        if (typeof onReset === "function") {
            onReset();
        }
    }

    handleReload() {
        if (typeof globalThis.window === "undefined") return;
        globalThis.window.location.reload();
    }

    render() {
        const { hasError, error } = this.state;
        const { children, fallback, title } = this.props;

        if (!hasError) return children;

        // Console-only errors: log was already emitted in componentDidCatch.
        // Attempt to re-render children instead of blocking the UI entirely.
        if (CONSOLE_ONLY_ERROR_PATTERNS.some((p) => error?.message?.includes(p))) {
            return children;
        }

        if (fallback) return fallback;

        return (
            <Result
                status="error"
                title={title}
                subTitle={
                    isDev && error?.message ? (
                        <Typography.Text type="secondary">
                            {error.message}
                        </Typography.Text>
                    ) : null
                }
                extra={
                    <Space>
                        <Button onClick={this.handleReset}>Try again</Button>
                        <Button type="primary" onClick={this.handleReload}>Reload page</Button>
                    </Space>
                }
            />
        );
    }
}
